import type { Business, ScoreSnapshot, Action } from '../contract';
import { SparklesIcon } from 'lucide-react';
import { PageHeader } from '../primitives/PageHeader';
import { Button } from '../primitives/Button';
import { TaskCard } from '../components/TaskCard';
import { useActionDrawer } from '../components/ActionDrawer';
import { useActionStatuses, makeTrackerScope } from '../trackerState';

const COLUMNS: Array<{ status: Action['status']; label: string }> = [
  { status: 'todo', label: 'To do' },
  { status: 'in_progress', label: 'In progress' },
  { status: 'done', label: 'Done' },
];

// Sorted by estLift, nulls last (§To-do).
const byLift = (a: Action, b: Action) => {
  if (a.estLift === null && b.estLift === null) return 0;
  if (a.estLift === null) return 1;
  if (b.estLift === null) return -1;
  return b.estLift - a.estLift;
};

export function ToDo({ snapshot, business, embedded }: { snapshot: ScoreSnapshot; business: Business; embedded?: boolean }) {
  const { openAction } = useActionDrawer();
  // Live statuses: approving or completing an action in the drawer moves its
  // card between columns automatically (session overrides over the snapshot),
  // scoped to this business + assessment run so drill-ins never share state
  // and a re-scan starts clean.
  const statusOf = useActionStatuses(makeTrackerScope(business.id, snapshot.capturedAt));
  return (
    <div>
      {!embedded && (
        <PageHeader
          title="To-do"
          subtitle={`The fixes that move ${business.name} the most, biggest first. Open any card to see exactly how it gets done.`}
          action={
            <Button>
              <SparklesIcon className="h-4 w-4" />
              Have ourteam do these
            </Button>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {COLUMNS.map((col) => {
          const items = snapshot.actions
            .map((a) => ({ ...a, status: statusOf(a) }))
            .filter((a) => a.status === col.status)
            .sort(byLift);
          return (
            <div key={col.status} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-3 flex items-center justify-between px-1 text-sm font-semibold text-gray-700">
                <span>{col.label}</span>
                <span className="text-gray-400 tabular-nums">{items.length}</span>
              </div>
              {items.length > 0 ? (
                items.map((a) => <TaskCard key={a.id} action={a} onOpen={() => openAction(a.id)} />)
              ) : (
                <p className="px-1 py-6 text-center text-sm text-gray-400">Nothing here.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
